import {AxiosInstance} from "axios";
import Transaction from "../model/transaction/Transaction";
import {TransactionEventApiDto} from "./model/rest";
import {
	default as TransactionEvent,
	TransactionEventType,
	TransactionEventTypeEnum
} from "../model/transaction/event/TransactionEvent";
import TransactionPaymentReceivedEvent from "../model/transaction/event/TransactionPaymentReceivedEvent";
import TransactionPaymentReconciledEvent from "../model/transaction/event/TransactionPaymentReconciledEvent";
import InvoiceSummary from "../model/invoice/InvoiceSummary";
import InvoicePaymentSummary from "../model/invoice/InvoicePaymentSummary";
import TransactionPaymentUnreconciledEvent from "../model/transaction/event/TransactionPaymentUnreconciledEvent";
import StripePayout from "../model/transaction/StripePayout";
import TransactionPaymentPartiallyReconciledEvent from "../model/transaction/event/TransactionPaymentPartiallyReconciledEvent";
import BillSummary from "../model/bill/BillSummary";
import BillPaymentSummary from "../model/bill/BillPaymentSummary";
import GoCardlessPayout from "../model/transaction/GoCardlessPayout";

/**
 * Transaction Api.
 */
export default class TransactionApi {
	constructor(public readonly axiosInstance: AxiosInstance) {}

	/**
	 * Fetch transaction for given id.
	 * @returns {Promise<Transaction[]>} Promise containing transaction.
	 */
	fetchTransaction(id: string): Promise<Transaction> {
		const url = `/api/web/accounting/transactions/${id}`;
		return this.axiosInstance.get(url).then(resp => Transaction.fromJson(resp.data));
	}

	/**
	 * Fetch events for transaction.
	 * @returns {Promise<Array<TransactionEventType>>}
	 * @param transactionId
	 */
	fetchTransactionEvents(transactionId: string): Promise<Array<TransactionEventType>> {
		const url = `/api/web/accounting/transactions/${transactionId}/events`;
		return this.axiosInstance.get(url).then(resp => resp.data.map((event: TransactionEventApiDto) => {
			switch (event.type) {
				case TransactionEventTypeEnum.PaymentReceived:
					return TransactionPaymentReceivedEvent.fromJson(event);
				case TransactionEventTypeEnum.PaymentReconciled:
					return TransactionPaymentReconciledEvent.fromJson(event);
				case TransactionEventTypeEnum.UndoReconciliation:
					return TransactionPaymentUnreconciledEvent.fromJson(event);
				case TransactionEventTypeEnum.PaymentPartiallyReconciled:
					return TransactionPaymentPartiallyReconciledEvent.fromJson(event);
				default:
					//fallback
					return null;
			}
		}).filter((item: TransactionEvent) => item !== null));
	}

	/**
	 * Fetch invoice recommendations for transaction.
	 * @param {string} transactionId
	 * @returns {Promise<Array<InvoiceSummary>>}
	 */
	fetchInvoiceRecommendationsForTransaction(transactionId: string): Promise<Array<InvoiceSummary>> {
		const url = `/api/web/accounting/transactions/${transactionId}/invoice_recommendations`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(InvoiceSummary.fromJson));
	}

	/**
	 * Fetch bill recommendations for transaction.
	 * @param {string} transactionId
	 * @returns {Promise<Array<BillSummary>>}
	 */
	fetchBillRecommendationsForTransaction(transactionId: string): Promise<Array<BillSummary>> {
		const url = `/api/web/accounting/transactions/${transactionId}/bill_recommendations`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(BillSummary.fromJson));
	}

	/**
	 * Fetch invoice payments info (invoices reconciled with transaction).
	 * @param {string} transactionId
	 * @returns {Promise<Array<InvoicePaymentSummary>>}
	 */
	fetchInvoicePaymentSummariesForTransaction(transactionId: string): Promise<Array<InvoicePaymentSummary>> {
		const url = `/api/web/accounting/transactions/${transactionId}/invoice_payments`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(InvoicePaymentSummary.fromJson));
	}

	/**
	 * Fetch bill payments info (bills reconciled with transaction).
	 * @param {string} transactionId
	 * @returns {Promise<Array<BillPaymentSummary>>}
	 */
	fetchBillPaymentSummariesForTransaction(transactionId: string): Promise<Array<BillPaymentSummary>> {
		const url = `/api/web/accounting/transactions/${transactionId}/bill_payments`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(BillPaymentSummary.fromJson));
	}

	/**
	 * Reconcile invoice
	 * @param {string} transactionId
	 * @param {string} invoiceId
	 * @returns {Promise<any>}
	 */
	reconcileInvoiceTransaction(transactionId: string, invoiceId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/invoice_reconcile`;
		return this.axiosInstance.put(url, {invoiceId});
	}

	/**
	 * Reconcile bill
	 * @param {string} transactionId
	 * @param billId
	 * @returns {Promise<any>}
	 */
	reconcileBillTransaction(transactionId: string, billId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/bill_reconcile`;
		return this.axiosInstance.put(url, {billId});
	}

	/**
	 * Undo reconcile invoice
	 * @param {string} transactionId
	 * @param {string} invoiceId
	 * @returns {Promise<any>}
	 */
	undoReconcileInvoiceTransaction(transactionId: string, invoiceId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/undo_invoice_reconciliation`;
		return this.axiosInstance.put(url, {invoiceId});
	}

	/**
	 * Undo reconcile bill
	 * @param {string} transactionId
	 * @param {string} billId
	 * @returns {Promise<any>}
	 */
	undoReconcileBillTransaction(transactionId: string, billId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/undo_bill_reconciliation`;
		return this.axiosInstance.put(url, {billId});
	}

	/**
	 * Ignore transaction.
	 * @param {string} transactionId
	 * @returns {Promise<any>}
	 */
	ignoreTransaction(transactionId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/ignore`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Undo ignore transaction.
	 * @param {string} transactionId
	 * @returns {Promise<any>}
	 */
	undoIgnoreTransaction(transactionId: string): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/undo_ignore`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Fetch stripe payout.
	 * @param {string} transactionId
	 */
	fetchStripePayout(transactionId: string) {
		const url = `/api/web/accounting/transactions/stripe_payouts/${transactionId}`;
		return this.axiosInstance.get(url).then(resp => StripePayout.fromJson(resp.data));
	}

	/**
	 * Fetch stripe payout.
	 * @param {string} transactionId
	 */
	fetchGoCardlessPayout(transactionId: string) {
		const url = `/api/web/accounting/transactions/gocardless_payouts/${transactionId}`;
		return this.axiosInstance.get(url).then(resp => GoCardlessPayout.fromJson(resp.data));
	}
}
